import React from 'react'
import { Box } from '@chakra-ui/react'
import type { ButtonProps } from '@chakra-ui/react'

import MenuSelect from '~components/shared/form/menu-select'
import MenuOptionWthDescription from '~components/shared/texts/menu-option-with-description'
import {
    ClinicFieldsFragment,
    ClinicGroupFieldsFragment,
    useQueryClinicGroupClinicsQuery,
} from '~graphql/generated/graphql'
import { eqStringFilter } from '~utils/query-helpers'
import GenericMultiselect from './form/generic-multiselect'

interface Props {
    clinicGroup: ClinicGroupFieldsFragment
    clinic: ClinicFieldsFragment | undefined
    setClinic: (clinic: ClinicFieldsFragment | undefined) => void
}

export default function ClinicGroupClinicSelect({
    clinicGroup,
    clinic,
    setClinic,
}: Props) {
    const { data, loading } = useQueryClinicGroupClinicsQuery({
        variables: {
            where: [eqStringFilter('clinic_group', clinicGroup.id)],
        },
    })

    const clinics = (data?.queryClinicGroupClinics.map(c => c.clinic) || []).sort((a, b) => a?.name?.localeCompare(b?.name || '') || 0)

    return (
        <MenuSelect
            label="Select a Clinic"
            placeholder="Select a clinic"
            options={clinics.map(c => ({
                value: c.id,
                label: c.name,
            }))}
            containerProps={{
                w: 'full',
            }}
            w="full"
            value={clinic?.id}
            setValue={val =>
                setClinic(
                    clinics.find(c => c.id === val) as ClinicFieldsFragment,
                )
            }
            isDisabled={loading}
            renderValue={val => clinics.find(c => c.id === val)?.name}
            renderOption={val => (
                <MenuOptionWthDescription
                    label={clinics.find(c => c.id === val)?.name || ''}
                    description={
                        clinics.find(c => c.id === val)?.address?.street_a || ''
                    }
                />
            )}
        />
    )
}

interface ClinicGroupClinicMultiSelectProps {
    clinicGroup: ClinicGroupFieldsFragment
    clinics: ClinicFieldsFragment[]
    setClinics: (clinics: ClinicFieldsFragment[]) => void
    buttonOverrides?: ButtonProps
}

export function ClinicGroupClinicMultiSelect({
    clinicGroup,
    clinics,
    setClinics,
    buttonOverrides,
}: ClinicGroupClinicMultiSelectProps) {
    const { data, loading } = useQueryClinicGroupClinicsQuery({
        variables: {
            where: [eqStringFilter('clinic_group', clinicGroup?.id)],
        },
        skip: !clinicGroup,
    })

    const clinicOptions = data?.queryClinicGroupClinics.map(c => c.clinic) || []

    return (
        <GenericMultiselect
            options={clinicOptions as ClinicFieldsFragment[]}
            isLoading={loading}
            placeholder="Filter by clinic"
            size="md"
            menuProps={{
                // @ts-ignore
                zIndex: 9999,
                rounded: 'xl',
            }}
            selectedValues={clinics}
            setSelectedValues={setClinics}
            displayTransformer={clinic => `${clinic.name}`}
            searchByFields={['name']}
            listItem={clinic => (
                <Box as="span" fontSize="sm" fontWeight="medium">
                    {clinic.name}
                </Box>
            )}
            {...buttonOverrides}
        />
    )
}
